/**
 * The environment's tab-title prefix, held outside React Query.
 *
 * WHY THIS EXISTS RATHER THAN A HOOK CALL. The first version had `useDocumentTitle` call
 * `useEnvironment`, which calls `useVersion`, which is a React Query hook. Every page in the
 * app sets its title, so that one line made every page component require a
 * `QueryClientProvider` — it broke 17 existing tests immediately and would have quietly
 * taxed every page test written afterwards. A hook that is called everywhere must not drag a
 * provider behind it.
 *
 * So the query is subscribed ONCE, by `useEnvironment` in the header badge, and the answer
 * is published here. `useDocumentTitle` reads it synchronously through
 * `useSyncExternalStore`, which is React's own API for exactly this, and needs no provider
 * and no mocking.
 *
 * Empty until known, which reads as production — the same fail-safe as the badge.
 */

let titlePrefix = ''
const listeners = new Set<() => void>()

export function setEnvTitlePrefix(next: string): void {
  if (next === titlePrefix) return
  titlePrefix = next
  for (const listener of listeners) listener()
}

export function getEnvTitlePrefix(): string {
  return titlePrefix
}

export function subscribeEnvTitlePrefix(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Tests only: forget what was published, so one case cannot leak into the next.
 *
 * It does NOT clear the listeners. An earlier version did, which silently unsubscribed any
 * component still mounted — the store would then accept a new value and nothing would
 * re-render, producing a failure that looks like a broken component rather than a broken
 * helper. Resetting the VALUE and notifying is the whole job.
 */
export function resetEnvTitlePrefix(): void {
  setEnvTitlePrefix('')
}
